import { MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';

import { Stack } from '@mui/material';

import clsx from 'clsx';

import AttachmentView from '../../../../../../../components/AttachmentView';
import ImagePreviewDialog from '../../../../../../../components/ImagePreviewDialog';
import RichTextView from '../../../../../../../components/RichText/View';
import { AttachmentType } from '../../../../../../../db/shared/types';
import { i18nNS } from '../../../../../../../i18n';
import { getCommentElId, getCommentTextElId } from '../../../../../../helpers';
import CommentStatsDialog from './CommentStatsDialog';
import {
  Actions,
  commentClasses,
  ImageButton,
  RemovedCommentContent,
  Root,
  StyledCommentStatsButton,
  StyledImage,
} from './styles';
import useCommentVM, { Props as VMProps } from './vm';

interface Props extends VMProps {
  isOnlyCommentInGroup: boolean;
  className?: string;
}

const Comment = ({ commentId, isOnlyCommentInGroup, className }: Props) => {
  const { t } = useTranslation([i18nNS.COMMENTS]);
  const {
    isScreenReaderOn,
    comment,
    commentStatsAriaLabel,
    attachment,
    handleCommentClick,
    commentStatsButtonRef,
    isCommentStatsDialogOpen,
    handleCloseStatsDialog,
    handleStatsClick: _handleStatsClick,
    selectedCommentImagePreviewUrl,
    isImagePreviewDialogOpen,
    handleCloseImagePreviewDialog,
    handleImageClick: _handleImageClick,
  } = useCommentVM({ commentId });

  const handleStatsClick: MouseEventHandler = (event) => {
    event.stopPropagation();
    _handleStatsClick();
  };

  const handleImageClick =
    (previewUrl: string): MouseEventHandler =>
    (event) => {
      event.stopPropagation();
      _handleImageClick(previewUrl);
    };

  if (!comment) return null;

  const commentElId = getCommentElId(comment.id);
  const richTextElId = getCommentTextElId(comment.id);

  return (
    <>
      <Root
        role={isScreenReaderOn ? undefined : 'button'}
        className={clsx(
          {
            [commentClasses.clickable]: !comment.isDeleted,
          },
          className
        )}
        onClick={comment.isDeleted || isScreenReaderOn ? undefined : handleCommentClick}
      >
        {/* fake div is placed to increase the clickable area */}
        <div
          id={commentElId}
          tabIndex={isScreenReaderOn ? undefined : 0}
          className={clsx(commentClasses.fakeCommentArea, {
            [commentClasses.isFirstComment]: comment.isFirstCommentInGroup,
          })}
          aria-hidden
        />
        {comment.isDeleted ? (
          <RemovedCommentContent>{comment.message}</RemovedCommentContent>
        ) : (
          <Stack gap={2} alignItems="flex-start">
            <div role="document" id={richTextElId} tabIndex={isScreenReaderOn ? 0 : undefined}>
              {comment.message && <RichTextView richtext={comment.message} />}
              {attachment?.type === AttachmentType.IMAGE && (
                <ImageButton onClick={handleImageClick(attachment.imageURL)}>
                  <StyledImage src={attachment.thumbURL} alt="attachment" />
                </ImageButton>
              )}
              {attachment?.type === AttachmentType.FILE && (
                <AttachmentView
                  attachment={{
                    originalName: attachment.originalName,
                    name: attachment.name,
                    extension: attachment.extension,
                  }}
                  requestConfig={attachment.requestConfig}
                  color="error"
                />
              )}
            </div>
            <Actions
              commentId={comment.id}
              onAwardClick={handleCommentClick}
              forceDisplay={isScreenReaderOn}
              className={clsx(commentClasses.actions, {
                [commentClasses.alignActionsWithCreator]: isOnlyCommentInGroup,
              })}
            />
            <StyledCommentStatsButton
              ref={commentStatsButtonRef}
              commentId={commentId}
              edge="start"
              aria-label={commentStatsAriaLabel}
              onClick={handleStatsClick}
            />
          </Stack>
        )}
      </Root>
      <CommentStatsDialog
        open={isCommentStatsDialogOpen}
        onClose={handleCloseStatsDialog}
        commentId={commentId}
      />
      {selectedCommentImagePreviewUrl && (
        <ImagePreviewDialog
          open={isImagePreviewDialogOpen}
          onClose={handleCloseImagePreviewDialog}
          src={selectedCommentImagePreviewUrl}
          alt={t('image_preview', { ns: i18nNS.COMMENTS })}
        />
      )}
    </>
  );
};

export default Comment;
